import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ListConnections } from './components/list-connections/list-connections.component';
import { ListItemsConnected } from './components/list-itens-conected/list-items-connected.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CreateConnectionModalComponent } from './components/modals/create-connection-modal/create-connection-modal.component';
import { AddSeriesModalComponent } from './components/modals/add-series-modal/add-series-modal.component';
import { HttpService } from 'src/app/service/http/http.service';
import { ToastrService } from 'ngx-toastr';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { NewModalConfirmationComponent } from 'src/app/shared/components/common/new-modal-confirmation/new-modal-confirmation.component';
import { Subscription, interval } from 'rxjs';
import { DatasetItem, PaginatedResponse, SeriesItem, SourceItem } from './models/lakehouse.model';

@Component({
  selector: 'app-conexoes',
  standalone: true,
  imports: [
    CommonModule,
    ListConnections,
    ListItemsConnected,
    SkeletonDirective
  ],
  templateUrl: './conexoes.component.html',
  styleUrls: ['./conexoes.component.scss']
})
export class ConexoesComponent implements OnInit, OnDestroy {
  sources = signal<SourceItem[]>([]);
  sourceSelectedId = signal<string>('');
  datasetsSelected = signal<DatasetItem[]>([]);
  seriesByDataset = signal<Record<string, SeriesItem[]>>({});
  expandedDatasetId = signal<string>('');

  isLoading = signal(false);
  isLoadingDatasets = signal(false);
  loadingSeriesDatasetId = signal<string>('');
  private pollingSubscription?: Subscription;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private httpService: HttpService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.isLoading.set(true);
    this.getSources();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private getSources(): void {
    const projectId = sessionStorage.getItem('projectId');
    const params = projectId ? [{ param: 'project_id', value: projectId }] : [];

    const request$ = params.length > 0
      ? this.httpService.genericGetParams<PaginatedResponse<SourceItem>>('sources', params)
      : this.httpService.genericGet<PaginatedResponse<SourceItem>>('sources');

    request$.subscribe({
      next: (response) => {
        this.sources.set(response.rows);
        this.isLoading.set(false);
      },
      error: () => {
        this.toastr.show('Erro ao buscar conexões', 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
        this.isLoading.set(false);
      }
    });
  }

  private refreshSourcesStatus(): void {
    const projectId = sessionStorage.getItem('projectId');
    const params = projectId ? [{ param: 'project_id', value: projectId }] : [];

    const request$ = params.length > 0
      ? this.httpService.genericGetParams<PaginatedResponse<SourceItem>>('sources', params)
      : this.httpService.genericGet<PaginatedResponse<SourceItem>>('sources');

    request$.subscribe({
      next: (response) => {
        this.sources.set(response.rows);
      },
      error: () => {
        // Falha silenciosa no polling
      }
    });
  }

  onSelectSource(sourceId: string): void {
    this.sourceSelectedId.set(sourceId);
    this.datasetsSelected.set([]);
    this.seriesByDataset.set({});
    this.expandedDatasetId.set('');
    sessionStorage.setItem('lastSelectedSource', sourceId);
    sessionStorage.removeItem('lastSelectedDataset');
    this.isLoadingDatasets.set(true);
    this.getDatasetsForSource(sourceId);
    this.startPolling();
  }

  onLoadSeries(event: string | { datasetId: string; expand?: boolean }): void {
    const datasetId = typeof event === 'string' ? event : event.datasetId;
    const expand = typeof event === 'string' ? true : event.expand !== false;

    if (expand) {
      this.expandedDatasetId.set(datasetId);
      sessionStorage.setItem('lastSelectedDataset', datasetId);
    }

    this.loadingSeriesDatasetId.set(datasetId);
    this.getSeriesForDataset(datasetId);
  }

  private getDatasetsForSource(sourceId: string, restoreDatasetId?: string): void {
    this.httpService.genericGetParams<PaginatedResponse<DatasetItem>>('datasets', [
      { param: 'source_id', value: sourceId }
    ]).subscribe({
      next: (response) => {
        this.datasetsSelected.set(response.rows);
        this.isLoadingDatasets.set(false);

        const datasetToRestore = restoreDatasetId ?? sessionStorage.getItem('lastSelectedDataset');
        if (datasetToRestore && response.rows.some(d => d._id === datasetToRestore)) {
          this.onLoadSeries(datasetToRestore);
        }
      },
      error: () => {
        this.toastr.show('Erro ao buscar datasets', 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
        this.isLoadingDatasets.set(false);
      }
    });
  }

  private getSeriesForDataset(datasetId: string): void {
    this.httpService.genericGetParams<PaginatedResponse<SeriesItem>>('series', [
      { param: 'dataset_id', value: datasetId }
    ]).subscribe({
      next: (response) => {
        this.seriesByDataset.update(current => ({ ...current, [datasetId]: response.rows }));
        this.loadingSeriesDatasetId.set('');
      },
      error: () => {
        this.toastr.show('Erro ao buscar séries', 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
        this.loadingSeriesDatasetId.set('');
      }
    });
  }

  private refreshCurrentView(): void {
    const sourceId = this.sourceSelectedId();
    if (!sourceId) return;

    this.getDatasetsForSource(sourceId);

    const loadedDatasetIds = Object.keys(this.seriesByDataset());
    loadedDatasetIds.forEach(datasetId => this.getSeriesForDataset(datasetId));
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSubscription = interval(180000).subscribe(() => {
      this.refreshCurrentView();
    });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  onCreate(): void {
    const dialogRef = this.dialog.open(CreateConnectionModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.getSources();
        this.showAddSeriesConfirmation(result.connectionId, result.data?.name);
      }
    });
  }

  private showAddSeriesConfirmation(connectionId: string, connectionName?: string): void {
    const confirmationDialog = this.dialog.open(NewModalConfirmationComponent, {
      width: '500px',
      data: {
        title: 'Conexão criada com sucesso!',
        description: 'Deseja adicionar um dataset à conexão criada?',
        showConfirmMessage: true,
        cancelButtonName: 'Não, obrigado',
        confirmButtonName: 'Sim, adicionar'
      }
    });

    confirmationDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.selectConnectionAndLoadDatasets(connectionId);
        this.openAddSeriesModal(connectionId, connectionName);
      } else {
        this.selectConnectionAndLoadDatasets(connectionId);
      }
    });
  }

  private selectConnectionAndLoadDatasets(connectionId: string): void {
    sessionStorage.setItem('lastSelectedSource', connectionId);
    this.sourceSelectedId.set(connectionId);
    this.datasetsSelected.set([]);
    this.seriesByDataset.set({});
    this.expandedDatasetId.set('');
    this.isLoadingDatasets.set(true);
    this.getDatasetsForSource(connectionId);
    this.startPolling();
  }

  private openAddSeriesModal(connectionId: string, connectionName?: string): void {
    const addSeriesDialog = this.dialog.open(AddSeriesModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        sourceId: connectionId,
        sourceName: connectionName
      }
    });

    addSeriesDialog.afterClosed().subscribe(result => {
      if (result && result.success) {
        const sourceId = this.sourceSelectedId();
        if (sourceId) {
          const newSeriesId = result.data?._id;
          const newDatasetId = result.data?.dataset_id;
          if (newSeriesId) {
            sessionStorage.setItem('lastSelectedSeries', newSeriesId);
          }
          if (newDatasetId) {
            sessionStorage.setItem('lastSelectedDataset', newDatasetId);
          }
          this.getDatasetsForSource(sourceId, newDatasetId);
        }
      }
    });
  }

  onDelete(id: string): void {
    this.dialog.open(NewModalConfirmationComponent, {
      width: '400px',
      data: {
        title: 'Deletar conexão',
        description: 'Tem certeza que deseja deletar a conexão?',
        showConfirmMessage: true,
        cancelButtonName: 'Cancelar',
        confirmButtonName: 'Deletar'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.isLoading.set(true);
        const isSelected = this.sourceSelectedId() === id;
        this.httpService.genericDelete('sources', id).subscribe({
          next: () => {
            this.toastr.show('Conexão deletada com sucesso', 'Sucesso', {
              timeOut: 6000,
              messageClass: 'success'
            });
            if (isSelected) {
              this.sourceSelectedId.set('');
              this.datasetsSelected.set([]);
              this.seriesByDataset.set({});
              this.expandedDatasetId.set('');
            }
            this.getSources();
          },
          error: () => {
            this.toastr.show('Erro ao deletar conexão', 'Erro', {
              timeOut: 6000,
              messageClass: 'error'
            });
            this.isLoading.set(false);
          }
        });
      }
    });
  }

  onToggleCollapse(_collapsed: boolean): void {
  }

  onRefreshConnectionsStatus(): void {
    this.refreshSourcesStatus();
  }

  onToggleDetails(id: string): void {
    this.router.navigate(['/conexoes/detalhes', id]);
  }

  onSeriesAdded(): void {
    this.refreshCurrentView();
  }

  onExecutionsStarted(): void {
    this.refreshCurrentView();
  }

  onSeriesDelete(seriesId: string): void {
    this.dialog.open(NewModalConfirmationComponent, {
      width: '400px',
      data: {
        title: 'Deletar série',
        description: 'Tem certeza que deseja deletar a série?',
        showConfirmMessage: true,
        cancelButtonName: 'Cancelar',
        confirmButtonName: 'Deletar'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.httpService.genericDelete('series', seriesId).subscribe({
          next: () => {
            this.toastr.show('Série deletada com sucesso', 'Sucesso', {
              timeOut: 6000,
              messageClass: 'success'
            });
            this.refreshCurrentView();
          },
          error: () => {
            this.toastr.show('Erro ao deletar série', 'Erro', {
              timeOut: 6000,
              messageClass: 'error'
            });
          }
        });
      }
    });
  }
}
